import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import { useCart } from '../services/CartContext';
import { authService } from '../services/authService';
import { addressService } from '../services/addressService';
import { orderService } from '../services/orderService';
import { couponService } from '../services/couponService';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const loadSweetAlert = () => {
  return new Promise((resolve) => {
    if (window.Swal) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function Checkout({ onNavigate }) {
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    cartTotal,
    clearCart,
  } = useCart();

  // Form State
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });

  const [errors, setErrors] = useState({});
  const [addressId, setAddressId] = useState(null);
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [tempAddress, setTempAddress] = useState({
    line: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState('');

  const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');

  useEffect(() => {
    if (userId) {
      // Fetch user profile
      authService.getUserProfile(userId)
        .then((res) => {
          if (res && res.success && res.data) {
            setForm((prev) => ({
              ...prev,
              fullName: res.data.fullName || '',
              email: res.data.email || '',
              phone: res.data.contactNumber || '',
            }));
          }
        })
        .catch((err) => console.error('Error fetching user profile in checkout:', err));

      // Fetch address
      addressService.getAddress(userId)
        .then((res) => {
          if (res && res.success && res.data) {
            setAddressId(res.data.id);
            setForm((prev) => ({
              ...prev,
              address: res.data.line || res.data.address || '',
              city: res.data.city || '',
              state: res.data.state || '',
              zip: res.data.pincode || res.data.zip || '',
            }));
            setErrors((prev) => {
              const next = { ...prev };
              delete next.address;
              delete next.city;
              delete next.state;
              delete next.zip;
              return next;
            });
          }
        })
        .catch((err) => console.error('Error fetching address in checkout:', err));
    }
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setTempAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setAddressLoading(true);
    setAddressError('');

    try {
      const hasExistingAddress = !!addressId;
      const payload = {
        userId: Number(userId),
        line: tempAddress.line?.trim() || '',
        city: tempAddress.city?.trim() || '',
        state: tempAddress.state?.trim() || '',
        pincode: tempAddress.pincode?.trim() || '',
      };

      if (!payload.line || !payload.city || !payload.state || !payload.pincode) {
        setAddressError('Please fill in all address fields.');
        setAddressLoading(false);
        return;
      }

      if (hasExistingAddress) {
        payload.id = addressId;
      }

      const response = hasExistingAddress
        ? await addressService.updateAddress(payload)
        : await addressService.saveAddress(payload);

      if (response && response.success) {
        const savedData = response.data || {};
        const newAddressId = savedData.id || addressId;
        if (newAddressId) {
          setAddressId(newAddressId);
        }

        const resolvedAddress = savedData.line || savedData.address || payload.line;
        const resolvedCity = savedData.city || payload.city;
        const resolvedState = savedData.state || payload.state;
        const resolvedZip = savedData.pincode || savedData.zip || payload.pincode;

        setForm((prev) => ({
          ...prev,
          address: resolvedAddress,
          city: resolvedCity,
          state: resolvedState,
          zip: resolvedZip,
        }));

        setErrors((prev) => {
          const next = { ...prev };
          delete next.address;
          delete next.city;
          delete next.state;
          delete next.zip;
          return next;
        });

        setIsAddressDialogOpen(false);
      } else {
        setAddressError(response?.message || `Failed to ${hasExistingAddress ? 'update' : 'save'} address.`);
      }
    } catch (err) {
      setAddressError(err?.message || `An error occurred while ${addressId ? 'updating' : 'saving'} the address.`);
    } finally {
      setAddressLoading(false);
    }
  };

  // Calculations
  const platformFee = Number((cartTotal * 0.015).toFixed(2)); // 1.5% Platform Fee
  const originalGstTax = Number((cartTotal * 0.05).toFixed(2)); // 5% GST
  const gstTax = 0;
  const grandTotal = Number((cartTotal + platformFee + gstTax).toFixed(2));

  const focusFirstError = (tempErrors) => {
    const errorKeys = Object.keys(tempErrors);
    if (errorKeys.length === 0) return;

    const firstError = errorKeys[0];

    if (['fullName', 'email', 'phone'].includes(firstError)) {
      const element = document.querySelector(`[name="${firstError}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => element.focus(), 150);
      }
    } else if (['address', 'city', 'state', 'zip'].includes(firstError)) {
      setTempAddress({
        line: form.address || '',
        city: form.city || '',
        state: form.state || '',
        pincode: form.zip || '',
      });
      setAddressError('Please provide complete delivery address details.');
      setIsAddressDialogOpen(true);

      const fieldMap = {
        address: 'line',
        city: 'city',
        state: 'state',
        zip: 'pincode',
      };
      const dialogFieldName = fieldMap[firstError] || 'line';

      setTimeout(() => {
        const dialogElement = document.querySelector(`[name="${dialogFieldName}"]`);
        if (dialogElement) {
          dialogElement.focus();
        }
      }, 250);
    }
  };

  const handleValidation = () => {
    const tempErrors = {};
    if (!form.fullName.trim()) tempErrors.fullName = 'Full Name is required';
    if (!form.email.trim()) {
      tempErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      tempErrors.email = 'Enter a valid email';
    }
    if (!form.phone.trim()) {
      tempErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) {
      tempErrors.phone = 'Enter a valid 10-digit phone number';
    }
    if (!form.address.trim()) tempErrors.address = 'Address is required';
    if (!form.city.trim()) tempErrors.city = 'City is required';
    if (!form.state.trim()) tempErrors.state = 'State is required';
    if (!form.zip.trim()) {
      tempErrors.zip = 'ZIP code is required';
    } else if (!/^\d{6}$/.test(form.zip)) {
      tempErrors.zip = 'Enter a valid 6-digit ZIP code';
    }

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length > 0) {
      focusFirstError(tempErrors);
      return false;
    }
    return true;
  };

  const [paymentLoading, setPaymentLoading] = useState(false);

  const handlePlaceOrder = async () => {
    if (!handleValidation()) return;

    setPaymentLoading(true);

    try {
      const [isRazorpayLoaded, isSwalLoaded] = await Promise.all([
        loadRazorpayScript(),
        loadSweetAlert()
      ]);

      if (!isRazorpayLoaded || !isSwalLoaded) {
        alert('Failed to load required payment dependencies. Please check your connection.');
        setPaymentLoading(false);
        return;
      }

      const orderResponse = await orderService.createOrder(grandTotal);

      if (!orderResponse || !orderResponse.success || !orderResponse.data) {
        alert(orderResponse?.message || 'Failed to create order on server.');
        setPaymentLoading(false);
        return;
      }

      const orderData = JSON.parse(orderResponse.data);

      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY;
      if (!razorpayKey) {
        alert('Razorpay Key is missing!');
        setPaymentLoading(false);
        return;
      }

      const options = {
        key: razorpayKey,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'iSoret',
        description: 'Coupon Card Purchase',
        order_id: orderData.id,
        handler: async function (response) {
          try {
            const updatePayload = {
              userId: Number(userId),
              orderId: orderData.receipt,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              razorpayResponse: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            };

            const updateResult = await orderService.updateOrder(updatePayload);

            if (updateResult && updateResult.success) {
              let buyResult = null;
              try {
                const buyItems = cartItems.map((item) => ({
                  tier: (item.tier || item.name?.split(' ')[0] || '').toUpperCase(),
                  quantity: item.quantity,
                }));
                buyResult = await couponService.buyCoupons(buyItems);
              } catch (buyErr) {
                console.error('Error purchasing coupons:', buyErr);
                buyResult = {
                  success: false,
                  message: buyErr?.message || 'Failed to execute coupon purchase API call.',
                };
              }

              const succeeded = buyResult?.data?.succeeded || [];
              const succeededCodes = succeeded.map((c) => c.couponCode).filter(Boolean);

              // Toast Notification
              if (window.Swal) {
                const Toast = window.Swal.mixin({
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                  timer: 6000,
                  timerProgressBar: true,
                  background: '#153A35',
                  color: '#F5F1E6',
                  iconColor: '#D4A24C',
                });

                if (buyResult && buyResult.success) {
                  Toast.fire({
                    icon: 'success',
                    title: buyResult.message || 'Coupons purchased successfully.',
                    text: succeededCodes.length > 0 ? `Coupons: ${succeededCodes.join(', ')}` : '',
                  });
                } else {
                  Toast.fire({
                    icon: 'error',
                    title: 'Coupon Purchase Failed',
                    text: buyResult?.message || 'Could not complete coupon purchase.',
                  });
                }
              }

              const isSuccess = buyResult && buyResult.success;
              const couponHtml = succeededCodes.length > 0
                ? `<p style="margin-bottom: 12px; font-size: 15px;">${buyResult.message || 'Coupons purchased successfully.'}</p><div style="background: rgba(212, 162, 76, 0.15); border: 1px dashed #D4A24C; padding: 12px; border-radius: 8px; font-family: monospace; font-size: 15px; color: #D4A24C;"><strong>Coupon Codes:</strong><br/>${succeededCodes.join(', ')}</div>`
                : `<p style="font-size: 15px;">${buyResult?.message || 'Payment updated successfully.'}</p>`;

              window.Swal.fire({
                title: isSuccess ? 'Coupons Purchased!' : 'Payment Successful!',
                html: couponHtml,
                icon: isSuccess ? 'success' : 'warning',
                confirmButtonText: 'Scratch Now',
                background: '#153A35',
                color: '#F5F1E6',
                confirmButtonColor: '#D4A24C',
                backdrop: 'rgba(15, 46, 43, 0.75)',
                allowOutsideClick: false,
                allowEscapeKey: false,
                customClass: {
                  popup: 'swal-premium-popup',
                  title: 'swal-premium-title',
                  confirmButton: 'swal-premium-button'
                }
              }).then(() => {
                clearCart();
                if (onNavigate) onNavigate('my-coupon');
              });
            } else {
              alert(updateResult?.message || 'Payment received but failed to update order status on server. Please contact support.');
            }
          } catch (err) {
            console.error('Error updating order:', err);
            alert('An error occurred while updating the order status.');
          } finally {
            setPaymentLoading(false);
          }
        },
        prefill: {
          name: form.fullName,
          email: form.email,
          contact: form.phone,
        },
        theme: {
          color: '#0F2E2B',
        },
        modal: {
          ondismiss: function () {
            setPaymentLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Payment checkout error:', error);
      alert(error.message || 'An error occurred during payment checkout.');
      setPaymentLoading(false);
    }
  };

  // Styled TextField configuration helper
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(21, 58, 53, 0.4)',
      borderRadius: '12px',
      color: '#F5F1E6',
      '& fieldset': {
        borderColor: 'rgba(245, 241, 230, 0.14)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(245, 241, 230, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D4A24C',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(245, 241, 230, 0.5)',
      '&.Mui-focused': {
        color: '#D4A24C',
      },
    },
    '& .MuiFormHelperText-root': {
      color: '#E4572E',
    },
    mb: 2.5,
  };

  if (cartItems.length === 0) {
    return (
      <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: '50%',
              background: 'conic-gradient(from 180deg, #D4A24C, #E4572E, #2F8F76, #D4A24C)',
              display: 'grid',
              placeItems: 'center',
              mb: 4,
              mx: 'auto',
              position: 'relative',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 3,
                backgroundColor: '#0F2E2B',
                borderRadius: '50%',
                zIndex: 1,
              },
            }}
          >
            <ShoppingBagOutlinedIcon sx={{ fontSize: 40, color: '#D4A24C', zIndex: 2 }} />
          </Box>
          <Typography variant="h4" sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, mb: 2 }}>
            Your Cart is Empty
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(245, 241, 230, 0.66)', mb: 4 }}>
            You cannot checkout without items. Go back and select some digital coupon cards!
          </Typography>
          <Button
            variant="contained"
            onClick={() => onNavigate && onNavigate('home')}
            sx={{
              backgroundColor: '#D4A24C',
              color: '#0F2E2B',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: '999px',
              '&:hover': { backgroundColor: '#E8C078' },
            }}
          >
            Go Back Home
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 8, background: '#0F2E2B', color: '#F5F1E6' }}>
      <Container maxWidth="lg">
        {/* Header navigation */}
        <Box sx={{ mb: 5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => onNavigate && onNavigate('home')}
            sx={{
              color: '#F5F1E6',
              border: '1px solid rgba(245, 241, 230, 0.14)',
              '&:hover': { backgroundColor: 'rgba(245, 241, 230, 0.05)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
              Checkout
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.66)' }}>
              Complete your digital coupon card activation details
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '32px' }}>
          {/* Left Column: Items + Address */}
          <Box sx={{ flex: '1 1 65%' }}>

            {/* 1. Review Items Card */}
            <Box
              sx={{
                background: 'rgba(21, 58, 53, 0.3)',
                border: '1px solid rgba(245, 241, 230, 0.08)',
                borderRadius: '24px',
                p: { xs: 3, md: 4 },
                mb: 4,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <ShoppingBagOutlinedIcon sx={{ color: '#D4A24C' }} />
                1. Review Your Coupons
              </Typography>

              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {cartItems.map((item) => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      background: 'rgba(21, 58, 53, 0.5)',
                      border: '1px solid rgba(245, 241, 230, 0.08)',
                      borderRadius: '16px',
                      p: 2.5,
                      gap: 2.5,
                    }}
                  >
                    {/* Tier badge representation */}
                    <Box
                      sx={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '12px',
                        background: item.bg || 'linear-gradient(135deg, #D69A6C 0%, #A9673A 80%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0F2E2B',
                        fontWeight: 700,
                        fontFamily: '"Fraunces", serif',
                        fontSize: '12px',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                      }}
                    >
                      {item.tier}
                    </Box>

                    {/* Description */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: '18px' }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#D4A24C', fontWeight: 700, mt: 0.5, fontFamily: '"JetBrains Mono", monospace' }}>
                        ₹{item.price} each
                      </Typography>
                    </Box>

                    {/* Controls alignment */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: { xs: '100%', sm: 'auto' }, justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          sx={{
                            color: 'rgba(245, 241, 230, 0.7)',
                            border: '1px solid rgba(245, 241, 230, 0.15)',
                            p: 0.5,
                            '&:hover': { color: '#F5F1E6', borderColor: '#F5F1E6' },
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: '28px', textAlign: 'center', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', fontSize: '15px' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= 3}
                          sx={{
                            color: 'rgba(245, 241, 230, 0.7)',
                            border: '1px solid rgba(245, 241, 230, 0.15)',
                            p: 0.5,
                            '&:hover': { color: '#F5F1E6', borderColor: '#F5F1E6' },
                            '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.25)', borderColor: 'rgba(245, 241, 230, 0.08)' },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <IconButton
                        onClick={() => removeFromCart(item.id)}
                        sx={{
                          color: 'rgba(245, 241, 230, 0.4)',
                          '&:hover': { color: '#E4572E', backgroundColor: 'rgba(228, 87, 46, 0.08)' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* 2. Billing & Contact Address Card */}
            <Box
              sx={{
                background: 'rgba(21, 58, 53, 0.3)',
                border: '1px solid rgba(245, 241, 230, 0.08)',
                borderRadius: '24px',
                p: { xs: 3, md: 4 },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <LocationOnOutlinedIcon sx={{ color: '#D4A24C' }} />
                2. Contact & Billing Address
              </Typography>

              <Box component="form" noValidate>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleInputChange}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                      sx={inputStyle}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      sx={inputStyle}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={form.phone}
                      onChange={handleInputChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      sx={inputStyle}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#F5F1E6' }}>
                        Delivery Address
                      </Typography>
                      {form.address ? (
                        <IconButton
                          onClick={() => {
                            setTempAddress({
                              line: form.address,
                              city: form.city,
                              state: form.state,
                              pincode: form.zip,
                            });
                            setAddressError('');
                            setIsAddressDialogOpen(true);
                          }}
                          size="small"
                          sx={{
                            color: '#D4A24C',
                            border: '1px solid rgba(212, 162, 76, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(212, 162, 76, 0.1)',
                              borderColor: '#D4A24C',
                            },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => {
                            setTempAddress({ line: '', city: '', state: '', pincode: '' });
                            setAddressError('');
                            setIsAddressDialogOpen(true);
                          }}
                          size="small"
                          sx={{
                            color: '#D4A24C',
                            border: '1px solid rgba(212, 162, 76, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(212, 162, 76, 0.1)',
                              borderColor: '#D4A24C',
                            },
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {form.address ? (
                      <Box
                        sx={{
                          background: 'rgba(15, 46, 43, 0.4)',
                          border: '1px solid rgba(245, 241, 230, 0.08)',
                          borderRadius: '12px',
                          p: 2.5,
                        }}
                      >
                        <Typography variant="body1" sx={{ color: '#F5F1E6', fontWeight: 500 }}>
                          {form.address}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.6)', mt: 0.5 }}>
                          {form.city}, {form.state} - {form.zip}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          background: 'rgba(15, 46, 43, 0.2)',
                          border: '1px dashed rgba(245, 241, 230, 0.12)',
                          borderRadius: '12px',
                          p: 3,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.5)', mb: 2 }}>
                          No address added yet. Please click "Add Address" to provide your delivery details.
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => {
                            setTempAddress({ line: '', city: '', state: '', pincode: '' });
                            setAddressError('');
                            setIsAddressDialogOpen(true);
                          }}
                          sx={{
                            borderColor: '#D4A24C',
                            color: '#D4A24C',
                            borderRadius: '999px',
                            '&:hover': {
                              borderColor: '#E8C078',
                              backgroundColor: 'rgba(212, 162, 76, 0.05)',
                            },
                          }}
                        >
                          Add Address
                        </Button>
                      </Box>
                    )}
                    {(errors.address || errors.city || errors.state || errors.zip) && (
                      <Typography variant="caption" sx={{ color: '#E4572E', mt: 1, display: 'block' }}>
                        {errors.address || errors.city || errors.state || errors.zip}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>

          </Box>

          {/* Right Column: Billing summary */}
          <Box sx={{ flex: '1 1 35%', position: { md: 'sticky' }, top: '100px', height: 'fit-content' }}>
            <Box
              sx={{
                background: 'rgba(21, 58, 53, 0.5)',
                border: '1px solid rgba(245, 241, 230, 0.12)',
                borderRadius: '24px',
                p: 3.5,
                boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  mb: 3.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <ReceiptOutlinedIcon sx={{ color: '#D4A24C' }} />
                Order Summary
              </Typography>

              {/* Pricing breakdown */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '15.5px' }}>Subtotal</Typography>
                  <Typography sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', fontSize: '15.5px' }}>₹{cartTotal}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '15.5px' }}>Tax (5% GST)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ textDecoration: 'line-through', color: 'rgba(245, 241, 230, 0.4)', fontFamily: '"JetBrains Mono", monospace', fontSize: '15.5px' }}>
                      ₹{originalGstTax}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: '#4E9F3D', fontFamily: '"JetBrains Mono", monospace', fontSize: '15.5px' }}>
                      FREE
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '15.5px' }}>Platform Fee (1.5%)</Typography>
                  <Typography sx={{ fontWeight: 600, fontFamily: '"JetBrains Mono", monospace', fontSize: '15.5px' }}>+₹{platformFee}</Typography>
                </Box>

                <Divider sx={{ my: 1.5, borderColor: 'rgba(245, 241, 230, 0.1)' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>Total Amount</Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#D4A24C',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '22px',
                    }}
                  >
                    ₹{grandTotal}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                disabled={paymentLoading}
                onClick={handlePlaceOrder}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 700,
                  fontSize: '16px',
                  py: 1.6,
                  borderRadius: '999px',
                  mt: 4,
                  boxShadow: '0 8px 24px rgba(212, 162, 76, 0.25)',
                  '&:hover': {
                    backgroundColor: '#E8C078',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(212, 162, 76, 0.3)',
                    color: 'rgba(15, 46, 43, 0.5)',
                  }
                }}
              >
                {paymentLoading ? <CircularProgress size={24} color="inherit" /> : 'Pay & Activate Coupons'}
              </Button>
            </Box>
          </Box>
        </Box>
        {/* Add / Update Address Popup Dialog */}
        <Dialog
          open={isAddressDialogOpen}
          onClose={() => setIsAddressDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: 'rgba(15, 46, 43, 0.6)',
                backdropFilter: 'blur(4px)',
              }
            }
          }}
          PaperProps={{
            sx: {
              background: '#153A35',
              border: '1px solid rgba(245, 241, 230, 0.12)',
              borderRadius: '20px',
              color: '#F5F1E6',
              padding: 2,
            }
          }}
        >
          <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, pb: 1 }}>
            {addressId ? 'Edit Address Details' : 'Add New Address'}
          </DialogTitle>
          <form onSubmit={handleSaveAddress}>
            <DialogContent sx={{ pt: 1 }}>
              {addressError && (
                <Box
                  sx={{
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(228, 87, 46, 0.15)',
                    border: '1px solid #E4572E',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                    {addressError}
                  </Typography>
                </Box>
              )}
              <Grid container spacing={2.5}>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Address Line"
                    name="line"
                    value={tempAddress.line}
                    onChange={handleAddressChange}
                    required
                    placeholder="House No, Street name, Area"
                    sx={inputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={tempAddress.city}
                    onChange={handleAddressChange}
                    required
                    placeholder="City"
                    sx={inputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={tempAddress.state}
                    onChange={handleAddressChange}
                    required
                    placeholder="State"
                    sx={inputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    name="pincode"
                    value={tempAddress.pincode}
                    onChange={handleAddressChange}
                    required
                    placeholder="Pincode"
                    sx={inputStyle}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
              <Button
                onClick={() => setIsAddressDialogOpen(false)}
                sx={{
                  color: 'rgba(245, 241, 230, 0.6)',
                  '&:hover': { color: '#F5F1E6' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addressLoading}
                variant="contained"
                sx={{
                  borderRadius: '999px',
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  px: 3,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#E8C078',
                  },
                }}
              >
                {addressLoading ? <CircularProgress size={20} color="inherit" /> : (addressId ? 'Update' : 'Save Address')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Box>
  );
}
